// Comments on a published patch — anchored either to the patch as a
// whole or to a single voice (by JS const name).
//
// Why direct Supabase + not via the Worker: comments are CRUD-shaped
// and gate well on RLS alone. Routing them through the Worker would
// add latency and a second auth hop without buying anything — the
// fork endpoint exists in the Worker because it does multi-step work
// (read source → insert patch → insert lineage) where a server seam
// keeps the contract atomic from the client's view.

import { getSupabase } from '../auth/client';

export type Comment = {
  id: string;
  patchId: string;
  authorId: string;
  authorHandle: string;
  /** null = comment on the whole patch; otherwise the voice's
   *  source-stable JS const name (e.g. 'bass'). */
  voiceConstName: string | null;
  body: string;
  createdAt: string;
  deletedAt: string | null;
};

const VOICE_NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const MIN_BODY = 1;
const MAX_BODY = 2000;

export async function listComments(patchId: string): Promise<Comment[]> {
  const supabase = getSupabase();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('patch_comments')
    .select(
      'id, patch_id, author_id, voice_const_name, body, created_at, deleted_at, profiles:author_id(handle, display_name)',
    )
    .eq('patch_id', patchId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });
  if (error) throw new Error(`comments fetch failed: ${error.message}`);
  return (data ?? []).map(rowToComment);
}

export async function addComment(input: {
  patchId: string;
  voiceConstName: string | null;
  body: string;
}): Promise<Comment> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('auth not configured');
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData.session;
  if (!session) throw new Error('not signed in');

  const trimmed = input.body.trim();
  if (trimmed.length < MIN_BODY || trimmed.length > MAX_BODY) {
    throw new Error(`comment must be ${MIN_BODY}-${MAX_BODY} chars`);
  }
  if (input.voiceConstName !== null && !VOICE_NAME_RE.test(input.voiceConstName)) {
    throw new Error('voice name must be a valid JS identifier');
  }

  const { data, error } = await supabase
    .from('patch_comments')
    .insert({
      patch_id: input.patchId,
      author_id: session.user.id,
      voice_const_name: input.voiceConstName,
      body: trimmed,
    })
    .select(
      'id, patch_id, author_id, voice_const_name, body, created_at, deleted_at, profiles:author_id(handle, display_name)',
    )
    .single();
  if (error) throw new Error(`comment insert failed: ${error.message}`);
  return rowToComment(data);
}

/** Soft-delete: flips deleted_at. Author or patch-owner can do this
 *  (RLS update policy enforces). */
export async function deleteComment(commentId: string): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('auth not configured');
  const { error } = await supabase
    .from('patch_comments')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', commentId);
  if (error) throw new Error(`comment delete failed: ${error.message}`);
}

type ProfileShape = { handle?: string | null; display_name?: string | null };
type CommentRow = {
  id: string;
  patch_id: string;
  author_id: string;
  voice_const_name: string | null;
  body: string;
  created_at: string;
  deleted_at: string | null;
  // PostgREST embeds nested rows as either a single object or an array
  // depending on whether it can prove the FK is one-to-one. For
  // patch_comments → profiles via author_id it's the array variant; we
  // accept both shapes and pick the first row.
  profiles?: ProfileShape | ProfileShape[] | null;
};

function rowToComment(row: unknown): Comment {
  const r = row as CommentRow;
  const profile = pickProfile(r.profiles);
  return {
    id: r.id,
    patchId: r.patch_id,
    authorId: r.author_id,
    voiceConstName: r.voice_const_name,
    body: r.body,
    createdAt: r.created_at,
    deletedAt: r.deleted_at,
    authorHandle: profile?.handle ?? profile?.display_name ?? 'anon',
  };
}

function pickProfile(p: CommentRow['profiles']): ProfileShape | null {
  if (!p) return null;
  if (Array.isArray(p)) return p[0] ?? null;
  return p;
}
